import React from 'react';
import { Car, MapPin, Users, AlertTriangle, Phone } from 'lucide-react';

export const LogisticsPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="logistics-page">
      {/* Header */}
      <section className="py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-display text-5xl md:text-7xl mb-6 animate-fade-in text-[#e17c3b]">Logistique</h1>
          <p className="text-[#8d5f3f]/70 text-lg max-w-2xl mx-auto animate-fade-in delay-100">
            Tout ce qu'il faut savoir pour le jour J
          </p>
        </div>
      </section>

      {/* Organisation Section */}
      <section className="py-8 px-6 md:px-12" data-testid="organization-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-center mb-12 text-[#8d5f3f]">Organisation – Jour J</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Anticipez votre départ - Beige background with green sentence */}
            <div className="bg-[#fcf6f1] border border-[#e8ddd4] p-8 rounded-2xl text-center" data-testid="anticipez-block">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#e17c3b]/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-[#e17c3b]" />
                </div>
                <h3 className="font-display text-xl text-[#8d5f3f]">Anticipez votre départ</h3>
                <p className="text-[#8d5f3f]/70 text-sm">Prévoyez suffisamment de temps pour le trajet.</p>
                {/* Green sentence after the main text */}
                <p className="font-bold text-sm italic text-[#6b8e4e]">
                  Tout se déroule au même endroit et sans interruption.
                </p>
                <p className="text-[#8d5f3f]/80 text-sm">
                  Veuillez venir directement en tenue de soirée.
                </p>
              </div>
            </div>

            {/* Location Info - Beige background */}
            <div className="bg-[#fcf6f1] border border-[#e8ddd4] p-8 rounded-2xl text-center" data-testid="lieu-block">
              <div className="flex flex-col items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#e17c3b]/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#e17c3b]" />
                </div>
                <h3 className="font-display text-xl text-[#8d5f3f]">Lieu</h3>
              </div>
              <p className="text-[#8d5f3f]/90 leading-relaxed mb-4">
                Le lieu de réception se trouve à <strong className="text-[#8d5f3f]">20–30 minutes</strong> du centre de Séville.
              </p>
              <div className="flex items-center justify-center gap-3 p-4 bg-[#e17c3b]/10 rounded-xl">
                <Users className="w-5 h-5 text-[#e17c3b]" />
                <p className="text-sm text-[#8d5f3f] font-medium">
                  Événement réservé aux adultes
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transport Section */}
      <section className="py-16 px-6 md:px-12" data-testid="transport-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-center mb-12 text-[#8d5f3f]">Transports</h2>
          
          <div className="bg-[#fcf6f1] border border-[#e8ddd4] p-8 md:p-12 rounded-2xl shadow-md text-center">
            <div className="flex flex-col items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#e17c3b]/10 flex items-center justify-center">
                <Car className="w-7 h-7 text-[#e17c3b]" />
              </div>
              <div>
                <h3 className="font-display text-2xl text-[#8d5f3f]">Taxis / VTC recommandés</h3>
                <p className="text-[#8d5f3f]/70">Le moyen le plus pratique pour rejoindre le lieu</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-white rounded-xl border border-[#e8ddd4]">
                <p className="font-display text-3xl text-[#e17c3b] mb-2">~20 €</p>
                <p className="text-[#8d5f3f]/70 text-sm">par véhicule</p>
              </div>
              <div className="text-center p-6 bg-white rounded-xl border border-[#e8ddd4]">
                <p className="font-display text-3xl text-[#e17c3b] mb-2">3–4</p>
                <p className="text-[#8d5f3f]/70 text-sm">personnes par véhicule</p>
              </div>
              <div className="text-center p-6 bg-white rounded-xl border border-[#e8ddd4]">
                <p className="font-display text-lg text-[#e17c3b] mb-2">Applications</p>
                <p className="text-[#8d5f3f]/70 text-sm">Uber, Cabify, Taxis locaux</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wedding Planning Contact Block - Brown */}
      <section className="py-8 px-6 md:px-12" data-testid="contact-section">
        <div className="max-w-md mx-auto">
          <div className="bg-[#8d5f3f] p-8 rounded-2xl text-center shadow-md">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-display text-xl text-white">Wedding Planning – Antonia</h3>
              <a 
                href="tel:+34671298766"
                className="text-white/90 text-lg font-medium hover:text-white transition-colors"
              >
                +34 671 29 87 66
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Decorative Images - Logistique 1 & 2 */}
      <section className="py-8 px-6 md:px-12" data-testid="logistics-bottom-images">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_wedding-invite-gc/artifacts/3mfshzyc_Logistique%201.jpeg"
                alt="Logistique 1"
                className="w-full h-64 md:h-72 object-cover hover:scale-105 transition-transform duration-500"
                style={{ objectPosition: 'center 70%' }}
                loading="lazy"
                decoding="async"
                data-testid="logistics-bottom-image-1"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_wedding-invite-gc/artifacts/q0ejqpcu_logistique%202.jpeg"
                alt="Logistique 2"
                className="w-full h-64 md:h-72 object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
                decoding="async"
                data-testid="logistics-bottom-image-2"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
